import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Search, Loader, User, Phone, MapPin, Package, Calendar, AlertTriangle } from 'lucide-react';

const TechnicianCustomers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [outOfAreaMsg, setOutOfAreaMsg] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autocomplete: fetch suggestions as user types
  const fetchSuggestions = async (query) => {
    if (query.length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const res = await api.get(`/technicians/search-customers/?username=${encodeURIComponent(query)}`, {
        signal: abortControllerRef.current.signal
      });
      const data = res.data;
      if (data.out_of_area) {
        setSuggestions([]);
        setShowDropdown(false);
      } else {
        setSuggestions(data.customers || []);
        setShowDropdown((data.customers || []).length > 0);
      }
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.code !== 'ERR_CANCELED') {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setOutOfAreaMsg(null);
    setSearched(false);

    // Debounce autocomplete
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const selectCustomer = (customer) => {
    setSearchTerm(customer.username);
    setCustomers([customer]);
    setSearched(true);
    setShowDropdown(false);
    setOutOfAreaMsg(null);
  };

  const handleSearch = async () => {
    if (searchTerm.length < 1) return;
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setSearched(true);
    setShowDropdown(false);
    setOutOfAreaMsg(null);
    try {
      const res = await api.get(`/technicians/search-customers/?username=${encodeURIComponent(searchTerm)}`, {
        signal: abortControllerRef.current.signal
      });
      const data = res.data;
      if (data.out_of_area) {
        setCustomers([]);
        setOutOfAreaMsg(data.message);
      } else {
        setCustomers(data.customers || []);
        if ((data.customers || []).length === 0) {
          setOutOfAreaMsg(null);
        }
      }
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.code !== 'ERR_CANCELED') {
        console.error(err);
        setCustomers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Cleanup: abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Area Customers</h1>
        <p className="text-sm text-gray-500">Search customers in your assigned areas</p>
      </div>

      {/* Search Bar with Autocomplete */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex gap-3" ref={wrapperRef}>
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
              placeholder="Type username to search..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            {/* Autocomplete Dropdown */}
            {showDropdown && suggestions.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map(c => (
                  <button
                    key={c.id}
                    onClick={() => selectCustomer(c)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.username}</p>
                        <p className="text-xs text-gray-500">{c.name} - {c.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{c.area_name}</p>
                        <p className="text-xs text-gray-400">{c.package_name}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>
      </div>

      {/* Out of Area Message */}
      {outOfAreaMsg && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Customer Not in Your Area</p>
            <p className="text-sm text-amber-700 mt-1">{outOfAreaMsg}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {searched && !loading && customers.length === 0 && !outOfAreaMsg && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <User size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No customers found matching your search</p>
        </div>
      )}

      {customers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-blue-50 rounded-xl">
                  <User size={20} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm">{c.name}</h3>
                  <p className="text-xs text-gray-500 font-mono">{c.username}</p>
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Phone size={12} className="text-gray-400" /> {c.phone}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <MapPin size={12} className="text-gray-400" /> {c.area_name || 'N/A'}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Package size={12} className="text-gray-400" /> {c.package_name || 'N/A'}
                    </div>
                    {c.expiry_date && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Calendar size={12} className="text-gray-400" /> Expires: {c.expiry_date}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!searched && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Search size={48} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">Start typing a username to search</p>
        </div>
      )}
    </div>
  );
};

export default TechnicianCustomers;
